import { useCallback, useEffect, useState } from 'react';
import { TOption } from '@src/types';
import { getUsers } from '@src/services/users';

export const useTradingUserInfiniteOptions = () => {
  const [userOptions, setUserOptions] = useState<TOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [total, setTotal] = useState(0);

  const fetchUserOptions = useCallback(
    async (currentPage: number, isReset = false) => {
      setLoading(true);
      try {
        const res = await getUsers({ page: currentPage });
        const options = res.data.leeloo_users.map((user) => ({
          label: `${user.username || user.email || `User ${user.id}`} (${user.email})`,
          value: user.id.toString(),
        }));

        setUserOptions((prev) => {
          if (isReset) {
            return options as TOption[];
          }
          // Deduplicate based on value
          const existingValues = new Set(prev.map((opt) => opt.value));
          const newOptions = options.filter(
            (opt) => !existingValues.has(opt.value)
          );

          return [...prev, ...newOptions] as TOption[];
        });

        setHasMore(res.data.has_next);
        setTotal(res.data.total);
      } catch (error) {
        console.error('Failed to fetch trading users:', error);
        if (isReset) {
          setUserOptions([]);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    fetchUserOptions(1, true);
  }, []);

  const loadMore = useCallback(() => {
    if (!loading && hasMore) {
      const nextPage = page + 1;

      setPage(nextPage);
      fetchUserOptions(nextPage, false);
    }
  }, [loading, hasMore, page, fetchUserOptions]);

  const refresh = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchUserOptions(1, true);
  }, [fetchUserOptions]);

  return {
    userOptions,
    loading,
    hasMore,
    total,
    loadMore,
    refresh,
  };
};
